import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

// ===== STATUS CONFIG =====
export const STATUS_CONFIG = {
  nueva:             { label: 'Nueva',           bg: '#6ea8fe', color: '#fff', border: '#6ea8fe' },
  negociacion:       { label: 'Negociación',     bg: '#fbbf24', color: '#fff', border: '#fbbf24' },
  firma:             { label: 'Firma',           bg: '#a78bfa', color: '#fff', border: '#a78bfa' },
  'pedido-realizado':{ label: 'Pedido realizado',bg: '#34d399', color: '#fff', border: '#34d399' },
  'pedido-recibido': { label: 'Pedido entregado',bg: '#10b981', color: '#fff', border: '#10b981' },
  publicado:         { label: 'Publicado',       bg: '#f87171', color: '#fff', border: '#f87171' },
};

export const STATUS_ORDER = ['nueva','negociacion','firma','pedido-realizado','pedido-recibido','publicado'];

// ===== MESES DE PUBLICACIÓN =====
// Se guarda la clave ('enero'), no la etiqueta: igual que `status`, así el
// fichero de datos no queda atado a cómo se escribe el mes en pantalla.
// La lista de claves válidas está duplicada en server/lib/colaboraciones.js,
// que es quien valida lo que llega por la API. Si cambias una, cambia la otra.
export const MESES = {
  enero: 'Enero',
  febrero: 'Febrero',
  marzo: 'Marzo',
  abril: 'Abril',
  mayo: 'Mayo',
  junio: 'Junio',
  julio: 'Julio',
  agosto: 'Agosto',
  septiembre: 'Septiembre',
  octubre: 'Octubre',
  noviembre: 'Noviembre',
  diciembre: 'Diciembre',
};

// A mano y no con Object.keys(MESES): el orden de enero a diciembre es parte
// de lo que se muestra, no algo que deba depender del orden de inserción.
export const MESES_ORDEN = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// ===== REDUCER =====
// Sin filtro: se ven todas. El filtro por defecto escondía las publicadas, y
// eso solo tenía sentido mientras las tarjetas de resumen permitían cambiarlo;
// al quitarlas, dejarlo habría hecho invisibles las colaboraciones completadas.
const initialState = { collaborations: [], filter: null, searchQuery: '', loading: false };

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD':   return { ...state, collaborations: action.payload, loading: false };
    case 'ADD':    return { ...state, collaborations: [action.payload, ...state.collaborations] };
    case 'UPDATE': return { ...state, collaborations: state.collaborations.map(c => c.id === action.payload.id ? { ...c, ...action.payload } : c) };
    case 'DELETE': return { ...state, collaborations: state.collaborations.filter(c => c.id !== action.payload) };
    case 'REORDER': return { ...state, collaborations: action.payload };
    case 'SET_FILTER': return { ...state, filter: state.filter === action.payload ? null : action.payload };
    case 'SET_SEARCH': return { ...state, searchQuery: action.payload };
    case 'SET_LOADING': return { ...state, loading: action.payload };
    default: return state;
  }
}

// ===== CONTEXT =====
const CollaborationsContext = createContext(null);

export function CollaborationsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Carga inicial: intenta API, si falla usa localStorage
  useEffect(() => {
    async function load() {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const data = await apiFetch('/api/collaborations');
        dispatch({ type: 'LOAD', payload: data });
        // Sincroniza localStorage como caché
        localStorage.setItem('influencer_collaborations', JSON.stringify(data));
      } catch (err) {
        console.warn('API no disponible, usando localStorage:', err.message);
        try {
          const stored = localStorage.getItem('influencer_collaborations');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
              dispatch({ type: 'LOAD', payload: parsed });
              return;
            }
          }
        } catch {}
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
    load();
  }, []);

  // ===== ACTIONS =====
  const addCollaboration = useCallback(async (data) => {
    const newCollab = {
      id: Date.now().toString(),
      ...data,
      status: 'nueva',
      notes: '',
      contract: null,
      invoice: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // Optimistic update
    dispatch({ type: 'ADD', payload: newCollab });
    // Sync localStorage
    const updated = [newCollab, ...state.collaborations];
    localStorage.setItem('influencer_collaborations', JSON.stringify(updated));
    // Try API
    try { await apiFetch('/api/collaborations', { method: 'POST', body: JSON.stringify(newCollab) }); }
    catch (err) { console.warn('API write failed (saved locally):', err.message); }
    return newCollab;
  }, [state.collaborations]);

  const updateCollaboration = useCallback(async (id, updates) => {
    // Optimista: la interfaz responde al momento.
    const payload = { id, ...updates, updatedAt: new Date().toISOString() };
    dispatch({ type: 'UPDATE', payload });
    try {
      const guardado = await apiFetch(`/api/collaborations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      // Y luego manda lo que devuelve el servidor, que no siempre es lo que se
      // envió: los productos se rehacen desde el catálogo, así que del PATCH
      // salen con nombre y precio donde solo se mandaron los ids.
      if (guardado && guardado.id) dispatch({ type: 'UPDATE', payload: guardado });
    } catch (err) {
      console.warn('API update failed:', err.message);
    }
  }, []);

  // Para respuestas en las que el servidor ya es la fuente de verdad (subida de
  // adjuntos): refresca el estado local sin volver a escribir en la API.
  const applyCollaboration = useCallback((collab) => {
    dispatch({ type: 'UPDATE', payload: collab });
  }, []);

  // Una colaboración que ya creó el servidor (al aceptar una solicitud): entra
  // en la lista sin volver a darla de alta.
  const insertCollaboration = useCallback((collab) => {
    dispatch({ type: 'ADD', payload: collab });
  }, []);

  const reorderCollaborations = useCallback((newOrder) => {
    dispatch({ type: 'REORDER', payload: newOrder });
    localStorage.setItem('influencer_collaborations', JSON.stringify(newOrder));
    try { apiFetch('/api/collaborations/reorder', { method: 'POST', body: JSON.stringify({ ids: newOrder.map(c => c.id) }) }); }
    catch {} 
  }, []);

  const deleteCollaboration = useCallback(async (id) => {
    dispatch({ type: 'DELETE', payload: id });
    try { await apiFetch(`/api/collaborations/${id}`, { method: 'DELETE' }); }
    catch (err) { console.warn('API delete failed:', err.message); }
  }, []);

  const setFilter = useCallback((filter) => {
    dispatch({ type: 'SET_FILTER', payload: filter });
  }, []);

  const setSearchQuery = useCallback((query) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  }, []);

  // `filter`, cuando se usa, es un estado de STATUS_ORDER. Los valores
  // especiales que había antes ("__active__", "__total__"...) los inventaban las
  // tarjetas de resumen, que ya no existen. Hoy nadie lo fija: se ven todas.
  const filteredCollaborations = state.collaborations.filter((c) => {
    const matchesFilter = !state.filter || c.status === state.filter;
    const matchesSearch =
      !state.searchQuery || c.name.toLowerCase().includes(state.searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <CollaborationsContext.Provider value={{
      collaborations: state.collaborations,
      filteredCollaborations,
      filter: state.filter,
      searchQuery: state.searchQuery,
      loading: state.loading,
      addCollaboration, updateCollaboration, applyCollaboration, insertCollaboration,
      deleteCollaboration, reorderCollaborations, setFilter, setSearchQuery,
    }}>
      {children}
    </CollaborationsContext.Provider>
  );
}

export function useCollaborations() {
  const ctx = useContext(CollaborationsContext);
  if (!ctx) throw new Error('useCollaborations must be used within CollaborationsProvider');
  return ctx;
}
